import React, { useEffect, useState } from "react";
import { Plus, Trash2, Ticket } from "lucide-react";
import api, { inr, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", min_order: "", description: "" });

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code || !form.value) { toast.error("Code and value are required"); return; }
    try {
      await api.post("/admin/coupons", {
        code: form.code, type: form.type, value: Number(form.value),
        min_order: Number(form.min_order || 0), active: true, description: form.description,
      });
      toast.success("Coupon saved"); setForm({ code: "", type: "percent", value: "", min_order: "", description: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); toast.success("Coupon deleted"); load(); };

  return (
    <div className="fade-in" data-testid="admin-coupons">
      <h1 className="font-serif text-4xl text-forest mb-1">Coupons & Vouchers</h1>
      <p className="text-muted-foreground text-sm mb-8">Create discount codes and gift vouchers</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="bg-white border border-border rounded-xl p-6 space-y-4 h-fit" data-testid="coupon-form">
          <h3 className="font-serif text-xl text-forest">New Coupon</h3>
          <Input placeholder="CODE (e.g. DIWALI25)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="cf-code" />
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="cf-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="percent">Percentage (%)</SelectItem><SelectItem value="flat">Flat (₹)</SelectItem></SelectContent>
          </Select>
          <Input type="number" placeholder={form.type === "percent" ? "Discount %" : "Discount ₹"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="cf-value" />
          <Input type="number" placeholder="Minimum order ₹" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} data-testid="cf-min" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cf-desc" />
          <Button type="submit" data-testid="cf-save" className="w-full bg-forest text-cream rounded-full"><Plus size={16} className="mr-1" /> Create Coupon</Button>
        </form>

        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 h-fit">
          {coupons.length === 0 ? <p className="text-muted-foreground">No coupons yet.</p> : coupons.map((c) => (
            <div key={c.id} className="bg-white border border-dashed border-gold rounded-xl p-5 relative" data-testid={`coupon-${c.code}`}>
              <button onClick={() => del(c.id)} data-testid={`coupon-delete-${c.code}`} className="absolute top-3 right-3 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
              <div className="flex items-center gap-2 mb-2"><Ticket size={18} className="text-gold" /><span className="font-serif text-2xl text-forest">{c.code}</span></div>
              <div className="text-lg text-forest">{c.type === "percent" ? `${c.value}% OFF` : `${inr(c.value)} OFF`}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.description || `Min order ${inr(c.min_order)}`}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
